/* eslint-disable react-hooks/exhaustive-deps */
import { ENTITY } from "constants/entities";
import {
  ADDITIONAL_ITEMS_URL,
  ARTICLES_URL,
  ASSOCIATED_CLIENTS_URL,
  CASCADE_STARTS_URL,
  CLIENTS_URL,
  CONTAINERS_URL,
  GROUPS_URL,
  LOCATIONS_URL,
  STORES_URL,
  SUBLOCATIONS_URL,
  UNITS_URL,
  USERS_URL,
} from "constants/urls";
import useCrud from "hooks/useCrud";
import { useUser } from "hooks/useUser";
import { STATUS_CODES } from "constants/index";

import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  FC,
  useEffect,
} from "react";
import {
  AdditionalItem,
  AssociatedClient,
  Client,
  Container,
  Store,
  Unit,
  User,
  Location,
  Article,
  CascadeStart,
  Group,
  SubLocation,
} from "types";

type UserContext = {
  userData: User | null;
  isLoadingUser: boolean;
  stores: Store[];
  additionalItems: AdditionalItem[];
  clients: Client[];
  units: Unit[];
  associatedClients: AssociatedClient[];
  containers: Container[];
  locations: Location[];
  sublocations: SubLocation[];
  articles: Article[];
  users: User[];
  cascadeStarts: CascadeStart[];
  groups: Group[];
  addSessionData: (entity: string, data: SessionEntity) => void;
  addMultipleSessionData: (keys: string[], values: SessionEntity[]) => void;
  updateSessionData: (entity: string, data: SessionEntity) => void;
  updateMultipleSessionData: (entity: string[], data: SessionEntity[]) => void;
  deleteSessionData: (entity: string, ids: string[]) => void;
  logIn: (email: string, password: string) => Promise<boolean>;
  logOut: () => void;
  isLoggedIn: () => boolean;
};

export const SessionContext = createContext<UserContext>({
  userData: null,
  isLoadingUser: true,
  stores: [],
  additionalItems: [],
  clients: [],
  units: [],
  associatedClients: [],
  containers: [],
  locations: [],
  sublocations: [],
  articles: [],
  users: [],
  cascadeStarts: [],
  groups: [],
  addSessionData: (entity: string, data: SessionEntity) => {},
  addMultipleSessionData: (keys: string[], values: SessionEntity[]) => {},
  updateSessionData: (entity: string, data: SessionEntity) => {},
  updateMultipleSessionData: (keys: string[], values: SessionEntity[]) => {},
  deleteSessionData: (entity: string, ids: string[]) => {},
  logIn: (email: string, password: string) => Promise.resolve(false),
  logOut: () => {},
  isLoggedIn: () => false,
} as UserContext);

export type SessionEntity =
  | Store
  | Client
  | AdditionalItem
  | Unit
  | AssociatedClient
  | Container
  | Location
  | SubLocation
  | Article
  | User
  | CascadeStart
  | Group;

type SessionEntities = SessionEntity[];

export const useSession = () => useContext(SessionContext);

export const SessionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true); // Estado de carga
  const [sessionData, setSessionData] = useState<
    Record<string, SessionEntities>
  >({
    [ENTITY.STORES]: [],
    [ENTITY.CLIENTS]: [],
    [ENTITY.ADDITIONAL_ITEMS]: [],
    [ENTITY.UNITS]: [],
    [ENTITY.ASSOCIATED_CLIENTS]: [],
    [ENTITY.CONTAINERS]: [],
    [ENTITY.LOCATIONS]: [],
    [ENTITY.SUBLOCATIONS]: [],
    [ENTITY.ARTICLES]: [],
    [ENTITY.USERS]: [],
    [ENTITY.CASCADE_STARTS]: [],
    [ENTITY.GROUPS]: [],
  });

  const { fetchData: getStores } = useCrud<Store>(STORES_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getAdditionalItems } = useCrud<AdditionalItem>(
    ADDITIONAL_ITEMS_URL,
    { disableAutoFetch: true },
  );
  const { fetchData: getGroups } = useCrud<Group>(GROUPS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getClients } = useCrud<Client>(CLIENTS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getUnits } = useCrud<Unit>(UNITS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getAssociatedClients } = useCrud<AssociatedClient>(
    ASSOCIATED_CLIENTS_URL,
    { disableAutoFetch: true },
  );
  const { fetchData: getContainers } = useCrud<Container>(CONTAINERS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getLocations } = useCrud<SubLocation>(LOCATIONS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getSublocations } = useCrud<SubLocation>(SUBLOCATIONS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getArticles } = useCrud<Article>(ARTICLES_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getUsers } = useCrud<User>(USERS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getCascadeStarts } = useCrud<CascadeStart>(
    CASCADE_STARTS_URL,
    {
      disableAutoFetch: true,
    },
  );

  const addSessionData = (entity: string, data: SessionEntity) => {
    setSessionData((prev) => ({
      ...prev,
      [entity]: prev[entity].concat(data),
    }));
  };

  const addMultipleSessionData = (keys: string[], values: SessionEntity[]) => {
    setSessionData((prev) => {
      const newSessionData = { ...prev };
      keys.forEach((key, index) => {
        newSessionData[key] = newSessionData[key].concat(values[index]);
      });
      return newSessionData;
    });
  };

  const updateSessionData = (entity: string, data: SessionEntity) => {
    setSessionData((prev) => ({
      ...prev,
      [entity]: prev[entity].map((item: SessionEntity) =>
        item.id === data.id ? data : item,
      ),
    }));
  };

  const updateMultipleSessionData = (
    keys: string[],
    values: SessionEntity[],
  ) => {
    setSessionData((prev) => {
      const newSessionData = { ...prev };
      keys.forEach((key, index) => {
        newSessionData[key] = newSessionData[key].map((item: SessionEntity) =>
          item.id === values[index].id ? values[index] : item,
        );
      });
      return newSessionData;
    });
  };

  const deleteSessionData = (entity: string, ids: string[]) => {
    setSessionData((prev) => ({
      ...prev,
      [entity]: prev[entity].filter(
        (item: SessionEntity) => !ids.includes(item.id.toString()),
      ),
    }));
  };

  const { getCurrentUser, token, logIn, logOut, isLoggedIn } = useUser();

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoadingUser(true);
      const user = await getCurrentUser();
      if (user) {
        setUserData(user);
      } else {
        setUserData(null);
      }
      setIsLoadingUser(false);
    };

    if (token) {
      fetchUserData();
    } else {
      setUserData(null);
      setIsLoadingUser(false);
    }
  }, [token]);

  const initSessionData = async () => {
    const responses = await Promise.all([
      getStores(),
      getAdditionalItems(),
      getClients(),
      getUnits(),
      getAssociatedClients(),
      getContainers(),
      getLocations(),
      getSublocations(),
      getArticles(),
      getUsers(),
      getCascadeStarts(),
      getGroups(),
    ]);

    const [
      stores,
      additionalItems,
      clients,
      units,
      associatedClients,
      containers,
      locations,
      sublocations,
      articles,
      users,
      cascadeStarts,
      groups,
    ] = responses.map((response) => {
      if (response.status === STATUS_CODES.OK) {
        return response.data;
      }
      return [];
    });

    setSessionData({
      [ENTITY.STORES]: stores,
      [ENTITY.CLIENTS]: clients,
      [ENTITY.ADDITIONAL_ITEMS]: additionalItems,
      [ENTITY.UNITS]: units,
      [ENTITY.ASSOCIATED_CLIENTS]: associatedClients,
      [ENTITY.CONTAINERS]: containers,
      [ENTITY.LOCATIONS]: locations,
      [ENTITY.SUBLOCATIONS]: sublocations,
      [ENTITY.ARTICLES]: articles,
      [ENTITY.USERS]: users,
      [ENTITY.CASCADE_STARTS]: cascadeStarts,
      [ENTITY.GROUPS]: groups,
    });
  };

  useEffect(() => {
    if (token) {
      initSessionData();
    }
  }, [token]);

  return (
    <SessionContext.Provider
      value={{
        userData,
        isLoadingUser,
        stores: sessionData[ENTITY.STORES] as Store[],
        additionalItems: sessionData[
          ENTITY.ADDITIONAL_ITEMS
        ] as AdditionalItem[],
        clients: sessionData[ENTITY.CLIENTS] as Client[],
        units: sessionData[ENTITY.UNITS] as Unit[],
        associatedClients: sessionData[
          ENTITY.ASSOCIATED_CLIENTS
        ] as AssociatedClient[],
        containers: sessionData[ENTITY.CONTAINERS] as Container[],
        locations: sessionData[ENTITY.LOCATIONS] as Location[],
        sublocations: sessionData[ENTITY.SUBLOCATIONS] as SubLocation[],
        articles: sessionData[ENTITY.ARTICLES] as Article[],
        users: sessionData[ENTITY.USERS] as User[],
        cascadeStarts: sessionData[ENTITY.CASCADE_STARTS] as CascadeStart[],
        groups: sessionData[ENTITY.GROUPS] as Group[],
        addSessionData,
        addMultipleSessionData,
        updateSessionData,
        updateMultipleSessionData,
        deleteSessionData,
        logIn,
        logOut,
        isLoggedIn,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
